import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertOctagon } from 'lucide-react';

interface ConfirmCloseDialogProps {
  open: boolean;
  fyLabel: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (closingNotes: string) => void;
  submitting: boolean;
}

/**
 * Final-step confirmation for closing a financial year. Requires the operator to type
 * the FY label exactly to enable the submit button — same UX trick the lock dialog
 * uses, since close is irreversible (no reopen path; see SPEC D2).
 */
export const ConfirmCloseDialog = ({
  open,
  fyLabel,
  onOpenChange,
  onSubmit,
  submitting,
}: ConfirmCloseDialogProps) => {
  const [typedLabel, setTypedLabel] = useState('');
  const [notes, setNotes] = useState('');

  // Reset on close via the parent's onOpenChange path (no useEffect → setState anti-pattern).
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTypedLabel('');
      setNotes('');
    }
    onOpenChange(next);
  };

  const labelMatches = typedLabel.trim() === fyLabel;
  const canSubmit = labelMatches && notes.trim().length > 0 && !submitting;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertOctagon className="size-5" /> Confirm close — irreversible
          </DialogTitle>
          <DialogDescription>
            Once confirmed, <strong>{fyLabel}</strong> cannot be reopened. Adjustments to a closed year are
            posted forward as journal entries in the current open year.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="confirmation-label">
              Type <span className="font-mono font-semibold">{fyLabel}</span> to confirm
            </Label>
            <Input
              id="confirmation-label"
              value={typedLabel}
              onChange={(e) => setTypedLabel(e.target.value)}
              placeholder={fyLabel}
              autoComplete="off"
              disabled={submitting}
              className="mt-1 font-mono"
            />
            {typedLabel.length > 0 && !labelMatches && (
              <p className="text-xs text-red-600 mt-1">Doesn't match. Type the label exactly as shown.</p>
            )}
          </div>

          <div>
            <Label htmlFor="closing-notes">Closing notes (required)</Label>
            <Textarea
              id="closing-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Closing per Finance Manager review of 2025-12-31. All preflight items resolved."
              disabled={submitting}
              className="mt-1"
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(notes.trim())} disabled={!canSubmit}>
            {submitting ? 'Closing…' : 'Confirm Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
